import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { ProductContactCard } from '@repo/types';

import { createApiCall, instance } from 'services';
import { readAccessConsent, getLocationIfAlreadyPermitted } from 'utils/accessConsent';

/**
 * VRG-720 slice 3 -- the product's contact card, as the certificate page sees it.
 *
 * The card is NEVER in the public certificate payload; the server releases it only to a viewer
 * with access (the owner, current or previous; anyone when the product has no access gate; a
 * visitor who has submitted the gate's details). So there are two ways it reaches this page, and
 * both land in ONE cache entry so the section renders from a single source:
 *
 *   1. `useContactCard` asks `GET certificates/public/:id/contact-card`. Enabled only where the
 *      server could say yes (an owner, or an ungated product); a 403 resolves to "no card".
 *   2. For an access-GATED product, the card comes back in the response to the access POST the
 *      gate already makes (`POST certificates/public/:id/access`), and `storeContactCard` writes
 *      it into the same cache entry. That request is the only one that carries what the gate asks
 *      for, which is why the card is released there and not by a separate call.
 */

export const contactCardKey = (certificateId: string) => ['contact-card', certificateId] as const;

export type { ProductContactCard };

const fetchContactCard = async (certificateId: string): Promise<ProductContactCard | null> => {
  const response = await createApiCall({
    url: `certificates/public/${certificateId}/contact-card`,
  });
  if (response?.status !== 200) return null; // 403 = no access; anything else = nothing to show
  return (response.data?.contact_card as ProductContactCard | null) ?? null;
};

export const useContactCard = (certificateId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: contactCardKey(certificateId ?? ''),
    queryFn: () => fetchContactCard(certificateId as string),
    enabled: enabled && Boolean(certificateId),
    // A card changes when a brand edits the product, not mid-visit.
    staleTime: 5 * 60_000,
    retry: false,
  });

/**
 * Put a card the access POST returned into the cache. A `null` is ignored on purpose: it means
 * "this submission did not qualify, or there is no card", and must not overwrite a card an
 * earlier call already released.
 */
export const storeContactCard = (
  queryClient: QueryClient,
  certificateId: string,
  response: { data?: { contact_card?: ProductContactCard | null } } | undefined,
) => {
  const card = response?.data?.contact_card;
  if (card) queryClient.setQueryData(contactCardKey(certificateId), card);
};

/** Join the API base and an API-relative path with exactly one slash. */
const absoluteApiUrl = (path: string) => {
  const base = String(instance.defaults.baseURL ?? '').replace(/\/+$/, '');
  return `${base}/${path.replace(/^\/+/, '')}`;
};

/**
 * Ask for a signed link to the .vcf and return its absolute URL.
 *
 * The body carries what the access gate asked for, taken from the consent this visitor already
 * gave on this origin (the same bar `POST :id/access` holds them to): their email, and a fresh
 * position only when they previously consented to one AND the browser permission is still
 * granted (never a prompt). An owner or an ungated product needs neither and sends an empty body.
 */
export const requestContactCardUrl = async (certificateId: string): Promise<string> => {
  const consent = readAccessConsent();
  const body: Record<string, unknown> = {};
  if (consent?.email) body.email = consent.email;
  if (consent?.locationConsent) {
    const location = await getLocationIfAlreadyPermitted();
    if (location) {
      body.location = location;
      body.consent = true;
    }
  }

  const response = await createApiCall({
    method: 'POST',
    url: `certificates/public/${certificateId}/contact-card/link`,
    data: body,
    throwError: true,
  });
  const path = response.data?.path as string | undefined;
  if (!path) throw new Error('No contact card link returned');
  return absoluteApiUrl(path);
};
